import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import clsx from 'clsx';
import { InputText } from 'components/forms/InputText';
import { Button } from 'components/ui/Button';
import { toDayInput } from 'core/utils/dates';
import { NsKey } from 'enums/NsKey';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { Calculator } from 'lucide-react';
import { FC, useState } from 'react';
import {
  monthsBetweenDates,
  monthsBetweenInstalments
} from './loanDurationCalculator';

type CalculatorMode = 'dates' | 'instalments';

const toInstalmentNumber = (value: string): number | '' =>
  value === '' ? '' : Number(value);

interface LoanDurationCalculatorPanelProps {
  onApply: (months: number) => void;
  onClose: () => void;
}

// Mounted only while the popover is open: its state starts over at each opening.
const LoanDurationCalculatorPanel: FC<LoanDurationCalculatorPanelProps> = ({
  onApply,
  onClose
}) => {
  const { t } = useDynamicTranslation([NsKey.Borrower, NsKey.Common]);
  const [mode, setMode] = useState<CalculatorMode>('dates');
  const [firstDate, setFirstDate] = useState(toDayInput(new Date()));
  const [lastDate, setLastDate] = useState('');
  const [firstInstalment, setFirstInstalment] = useState<number | ''>(1);
  const [lastInstalment, setLastInstalment] = useState<number | ''>('');

  const months =
    mode === 'dates'
      ? monthsBetweenDates(firstDate, lastDate)
      : monthsBetweenInstalments(firstInstalment, lastInstalment);

  const modes: Array<{ value: CalculatorMode; label: string }> = [
    { value: 'dates', label: t('BORROWER_LOAN_FORM.CALCULATOR.BY_DATES') },
    {
      value: 'instalments',
      label: t('BORROWER_LOAN_FORM.CALCULATOR.BY_INSTALMENTS')
    }
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="font-semibold text-zinc-800">
        {t('BORROWER_LOAN_FORM.CALCULATOR.TITLE')}
      </div>

      <div className="flex rounded-lg border border-gray-300 p-1 gap-1">
        {modes.map(({ value, label }) => (
          <button
            key={value}
            type="button"
            onClick={() => setMode(value)}
            className={clsx(
              'flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              mode === value
                ? 'bg-primary text-white'
                : 'text-gray-700 hover:bg-gray-100'
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {mode === 'dates' ? (
        <>
          <InputText
            type="date"
            id="calculatorFirstDate"
            name="calculatorFirstDate"
            label={t('BORROWER_LOAN_FORM.CALCULATOR.FIRST_DATE')}
            value={firstDate}
            onChange={(e) => setFirstDate(e.target.value)}
          />
          <InputText
            type="date"
            id="calculatorLastDate"
            name="calculatorLastDate"
            label={t('BORROWER_LOAN_FORM.CALCULATOR.LAST_DATE')}
            value={lastDate}
            onChange={(e) => setLastDate(e.target.value)}
          />
        </>
      ) : (
        <>
          <InputText
            type="number"
            min={1}
            id="calculatorFirstInstalment"
            name="calculatorFirstInstalment"
            label={t('BORROWER_LOAN_FORM.CALCULATOR.FIRST_INSTALMENT')}
            value={firstInstalment}
            onChange={(e) =>
              setFirstInstalment(toInstalmentNumber(e.target.value))
            }
          />
          <InputText
            type="number"
            min={1}
            id="calculatorLastInstalment"
            name="calculatorLastInstalment"
            label={t('BORROWER_LOAN_FORM.CALCULATOR.LAST_INSTALMENT')}
            value={lastInstalment}
            onChange={(e) =>
              setLastInstalment(toInstalmentNumber(e.target.value))
            }
          />
        </>
      )}

      <div className="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-3 text-sm">
        <span className="text-gray-500">
          {t('BORROWER_LOAN_FORM.CALCULATOR.RESULT')}
        </span>
        <span className="font-semibold text-zinc-800">
          {months === null
            ? '—'
            : `${months} ${t('BORROWER_LOAN_FORM.CALCULATOR.MONTHS')}`}
        </span>
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
        <Button outline onClick={onClose}>
          {t('BORROWER_LOAN_FORM.CALCULATOR.CANCEL')}
        </Button>
        <Button
          color="primary"
          disabled={months === null}
          onClick={() => {
            if (months === null) return;
            onApply(months);
            onClose();
          }}
        >
          {t('BORROWER_LOAN_FORM.CALCULATOR.APPLY')}
        </Button>
      </div>
    </div>
  );
};

interface LoanDurationCalculatorProps {
  onApply: (months: number) => void;
}

export const LoanDurationCalculator: FC<LoanDurationCalculatorProps> = ({
  onApply
}) => {
  const { t } = useDynamicTranslation([NsKey.Borrower, NsKey.Common]);

  return (
    <Popover className="relative">
      <PopoverButton
        type="button"
        title={t('BORROWER_LOAN_FORM.CALCULATOR.TITLE')}
        className="flex h-12 w-12 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 hover:border-primary hover:text-primary transition-colors"
      >
        <Calculator className="size-5" />
      </PopoverButton>

      {/* Portal-rendered panel (escapes overflow-hidden parents such as the slide menu). */}
      <PopoverPanel
        anchor={{ to: 'bottom end', gap: 8, padding: 16 }}
        className="z-[100] p-4 bg-white rounded-lg shadow-xl border border-gray-200 w-96"
      >
        {({ close }) => (
          <LoanDurationCalculatorPanel onApply={onApply} onClose={close} />
        )}
      </PopoverPanel>
    </Popover>
  );
};
